<template>
  <div class="mt-3 px-1.5 gap-3 flex flex-col">
    <div>
      <div class="form-line">
        <img src="@/assets/my-calendar/calendar/card.svg" alt="card" class="mt-2"/>
        <Input
          v-model="taskTitle"
          data-testid="my-calendar.schedule-task-panel.title-input"
          placeholder="Add a title"
          class="w-full description-input"
        />
      </div>
    </div>
    <!-- description -->
    <div>
      <div class="form-line">
        <img class="mt-2" src="@/assets/my-calendar/calendar/subject.svg" alt="subject" />
        <Input
          v-model="description"
          data-testid="my-calendar.schedule-task-panel.description-input"
          placeholder="Add a description"
          class="w-full description-input"
        />
      </div>
    </div>
    <!-- assign to patient -->
    <div>
      <div class="form-line">
        <img class="mt-1" src="@/assets/my-calendar/calendar/people.svg" alt="people" />
        <div>
          <InviteSelect
            v-model="selectedPatient"
            data-testid="my-calendar.schedule-task-panel.patient-select"
            placeholder="Assign Task to Patient (Optional)"
            :options="patientsList"
            :disable-dropdown="disablePatientSelector"
            header="Suggested Patients"
            class="w-full patient-select mb-2"
          />
          <div class="note-font">Unassigned tasks go to your calendar. Assigned tasks appear in the patient's calendar.</div>
        </div>

      </div>
    </div>

    <!-- recurring -->
    <div>
      <div class="form-line mt-2">
        <span class="repeat-icon mt-2 w-6 h-6" aria-label="repeat" />
        <div class="repeat-row">
          <SingleSelector
            data-testid="my-calendar.schedule-task-panel.repeat-select"
            class="repeat-select"
            :placeholder="'Does not repeat'"
            :options="repeatOptions"
            size="medium"
            :model-value="selectedRepeat"
            @update:model-value="handleRepeatChange"
          />
        </div>
      </div>
    </div>

    <!-- due time -->
    <div>
      <div class="form-line mt-2">
        <img class="mt-2" src="@/assets/my-calendar/calendar/clock.svg" alt="clock" />
        <div class="flex gap-3">
          <div>
            <DatePicker
              v-model="selectedDate"
              test-id="my-calendar.schedule-task-panel.due-date-picker"
              :default="defaultDate"
              :placeholder="dueDateRequired ? 'Due Date' : 'Due Date (Optional)'"
              question=""
              class="task-date-picker"
              @handleDate="handleDateChange"
            />
          </div>
          <div class="w-[180px]">
            <SingleSelector
              data-testid="my-calendar.schedule-task-panel.due-time-select"
              class="time-select"
              :placeholder="'Due Time (Optional)'"
              :options="timeOptions"
              size="medium"
              :model-value="selectedDueTime"
              @update:model-value="handleDueTimeChange"
            />
          </div>
        </div>
      </div>
    </div>



  </div>
</template>

<script setup>
import InviteSelect from './schedule-components/InviteSelect.vue';
import DatePicker from '@/views/my-calendar/components/DatePicker.vue';
import Input from '@/NewLibrary/Input.vue'
import { getTimeOptions } from '@/views/my-calendar/mock-data';
import { ref, watch, computed, onMounted } from 'vue';
import moment from 'moment-timezone';
import { useStore } from 'vuex'
import { notifyUnreportedError } from '@/utils/notify';

const store = useStore()
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])
const userSettings = computed(() => store.getters['calendar/userSettings'])
const defaultAppointmentDuration = computed(() => (
  String(userSettings.value?.default_appointment_duration || '60')
))
const timeOptions = computed(() => getTimeOptions(defaultAppointmentDuration.value))
const props = defineProps({
  activeTab: {
    type: String,
    default: 'task'
  },
  disablePatientSelector: {
    type: Boolean,
    default: false,
  }
})

// Use Vuex getter for cached formatted list - computed only once in store
const patientsList = computed(() => store.getters['calendar/formattedPatientsList'])
const selectedPatient = ref('')
// need retructure with calendar event
const originalTaskInfo = computed(() => store.state.calendar.currentTaskInfo)

const resolveSelectedPatientId = (taskInfo = originalTaskInfo.value) => {
  if (!taskInfo) return ''
  if (taskInfo.selectedPatient) return taskInfo.selectedPatient
  return patientsList.value.find(p => p.calendar_id === taskInfo.calendar_id)?.id || ''
}

const description = ref(originalTaskInfo.value?.description || '')
const taskTitle = ref(originalTaskInfo.value?.title || '')
const emit = defineEmits(['update:disabled']);

const isEndOfDaySentinel = (dateValue) => {
  if (!dateValue) return false
  const m = moment.utc(dateValue).tz(calendarTimezone.value)
  if (!m.isValid()) return false
  return m.hour() === 23 && m.minute() === 59 && m.second() === 59
}

const defaultDate = computed(() => {
  if (!originalTaskInfo?.value?.due_date) return '';
  const startDate = moment.utc(originalTaskInfo.value.due_date).tz(calendarTimezone.value);

  return startDate.format('YYYY-MM-DD');
});

const defaultDueTime = computed(() => {
  if (!originalTaskInfo?.value?.due_date) return '';
  if (isEndOfDaySentinel(originalTaskInfo.value.due_date)) return '';
  const dueTime = moment.utc(originalTaskInfo.value.due_date).tz(calendarTimezone.value);

  return dueTime.format('hh:mma');
});

const selectedDate = ref(defaultDate.value)
const selectedDueTime = ref(defaultDueTime.value)
const selectedRepeat = ref('does_not_repeat')

const getWeekdayCode = (dateStr) => {
  if (!dateStr) return ''
  const m = moment.tz(dateStr, 'YYYY-MM-DD', calendarTimezone.value)
  if (!m.isValid()) return ''
  const map = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']
  return map[m.day()] || ''
}

const getNthWeekdayOfMonth = (dateStr) => {
  if (!dateStr) return null
  const m = moment.tz(dateStr, 'YYYY-MM-DD', calendarTimezone.value)
  if (!m.isValid()) return null
  return Math.ceil(m.date() / 7)
}

const getOrdinalSuffix = (n) => {
  const v = n % 100
  if (v >= 11 && v <= 13) return 'th'
  switch (n % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

const repeatOptions = computed(() => {
  const weekday = getWeekdayCode(selectedDate.value)
  const nth = getNthWeekdayOfMonth(selectedDate.value)
  const weekdayName = selectedDate.value
    ? moment.tz(selectedDate.value, 'YYYY-MM-DD', calendarTimezone.value).format('dddd')
    : ''

  const monthlyLabel = (nth && weekdayName)
    ? `Monthly on the ${nth}${getOrdinalSuffix(nth)} ${weekdayName}`
    : 'Monthly'
  const weeklyLabel = weekdayName ? `Weekly on ${weekdayName}` : 'Weekly'

  return [
    { value: 'does_not_repeat', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekday', label: 'Every weekday' },
    { value: 'weekly', label: weeklyLabel },
    { value: 'monthly', label: monthlyLabel },
    { value: 'annually', label: 'Annually' },
  ]
})

// Stable recurrence start (DTSTART), captured once when the panel opens so that
// re-syncing the form (e.g. editing the title) never shifts the recurrence start or
// produces noisy store updates. For an existing recurring task we keep its original
// DTSTART (see the originalTaskInfo watch below).
const recurrenceStartIso = ref(moment().utc().format('YYYYMMDDTHHmmss[Z]'))

// Produces a single-line rrule string e.g.
// FREQ=MONTHLY;BYDAY=TH;BYSETPOS=4;UNTIL=20271023T235959Z;DTSTART=20260923T130000Z
// The task has no separate end-date field, so the due date is used as UNTIL; the
// recurrence runs from DTSTART (the stable start) until the due date.
const buildRrule = (repeatValue, dateStr) => {
  if (!repeatValue || repeatValue === 'does_not_repeat') return null
  const weekday = getWeekdayCode(dateStr)
  const nth = getNthWeekdayOfMonth(dateStr)
  const m = dateStr ? moment.tz(dateStr, 'YYYY-MM-DD', calendarTimezone.value) : null
  if (!m || !m.isValid()) return null

  const dtstart = recurrenceStartIso.value
  // Interpret the due date as end-of-day in the calendar timezone (then to UTC) so the
  // last occurrence is not cut off early for non-UTC users (timezone/DST offsets).
  const until = moment.tz(dateStr, 'YYYY-MM-DD', calendarTimezone.value).endOf('day').utc().format('YYYYMMDDTHHmmss[Z]')

  let freqPart = null
  if (repeatValue === 'daily') freqPart = 'FREQ=DAILY'
  if (repeatValue === 'weekday') freqPart = 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'
  if (repeatValue === 'weekly' && weekday) freqPart = `FREQ=WEEKLY;BYDAY=${weekday}`
  if (repeatValue === 'monthly' && weekday && nth) freqPart = `FREQ=MONTHLY;BYDAY=${weekday};BYSETPOS=${nth}`
  if (repeatValue === 'annually') freqPart = `FREQ=YEARLY;BYMONTH=${m.month() + 1};BYMONTHDAY=${m.date()}`
  if (!freqPart) return null

  return `${freqPart};UNTIL=${until};DTSTART=${dtstart}`
}

const resolveRepeatFromRrule = (rrule) => {
  if (!rrule) return 'does_not_repeat'
  const raw = String(rrule).trim()
  const rrulePart = raw.toUpperCase().includes('RRULE:')
    ? raw.split(/RRULE:/i).pop()
    : raw
  const normalized = String(rrulePart).trim().toUpperCase()
  if (normalized.startsWith('FREQ=DAILY')) return 'daily'
  if (normalized.startsWith('FREQ=WEEKLY') && normalized.includes('BYDAY=MO,TU,WE,TH,FR')) return 'weekday'
  if (normalized.startsWith('FREQ=WEEKLY') && normalized.includes('BYDAY=')) return 'weekly'
  if (normalized.startsWith('FREQ=MONTHLY')) return 'monthly'
  if (normalized.startsWith('FREQ=YEARLY')) return 'annually'
  return 'does_not_repeat'
}

// Preserve an existing recurring task's original DTSTART so editing never shifts it
const resolveDtstartFromRrule = (rrule) => {
  if (!rrule) return ''
  const match = String(rrule).toUpperCase().match(/DTSTART[:=](\d{8}T\d{6}Z)/)
  return match ? match[1] : ''
}

const rruleValue = computed(() => buildRrule(selectedRepeat.value, selectedDate.value))

const handleDueTimeChange = (value) => { selectedDueTime.value = value }
const handleRepeatChange = (value) => { selectedRepeat.value = value }

// A recurring task needs a due date (the recurrence start), so it becomes required
const dueDateRequired = computed(() => selectedRepeat.value !== 'does_not_repeat')

// Disable submit until the task has a title; a recurring task also needs a due date
const disableBtn = computed(() => {
  if (!taskTitle.value) return true
  if (dueDateRequired.value && !selectedDate.value) return true
  return false
})
watch(disableBtn, (val) => emit('update:disabled', val), { immediate: true })
const handleDateChange = (newDate) => {
  selectedDate.value = newDate
}
const handleStartDate = (date, time) => {
  if (!date) return null

  if (!time) {
    const endOfDay = moment.tz(`${date} 23:59:59`, 'YYYY-MM-DD HH:mm:ss', calendarTimezone.value)
    if (!endOfDay.isValid()) return null
    return endOfDay.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  }

  const momentDate = moment.tz(`${date} ${time}`, 'YYYY-MM-DD hh:mma', calendarTimezone.value)
  if (!momentDate.isValid()) return null
  return momentDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

const createOrUpdateEventAction = computed(() => store.state.calendar.createOrUpdateEventAction)

onMounted(async () => {
  if (!userSettings.value) {
    await store.dispatch('calendar/getUserSettings');
  }
})

watch(createOrUpdateEventAction, async (newVal) => {
  if (!newVal || props.activeTab !== 'task') return

  store.commit('calendar/SET_LAST_SUBMIT_FAILED', false)
  store.dispatch('calendar/setTaskLoading', true)
  try {
    // Ensure selected patient has a calendar_id before submitting the task.
    // clinic_patients may now come back without calendar_id; create one on demand.
    if (selectedPatient.value) {
      const patient = patientsList.value.find(p => p.id === selectedPatient.value);
      if (patient && !patient.calendar_id) {
        await store.dispatch('calendar/ensurePatientCalendarId', selectedPatient.value);
      }
    }
    if (originalTaskInfo.value?.mode === 'edit') {
      const updateInput = {
        task_id: originalTaskInfo.value.task_id,
        title: taskTitle.value,
        description: description.value,
        due_date: handleStartDate(selectedDate.value, selectedDueTime.value),
        rrule: rruleValue.value,
        calendar_id: selectedPatient.value
          ? patientsList.value.find(p => p.id === selectedPatient.value)?.calendar_id
          : store.state.calendar.creatorCalendarId
      }

      await store.dispatch('calendar/updateTask', updateInput)
    } else {
      const taskInfo = {
        calendar_id: selectedPatient.value
          ? patientsList.value.find(p => p.id === selectedPatient.value)?.calendar_id
          : store.state.calendar.creatorCalendarId,
        title: taskTitle.value,
        description: description.value,
        due_date: handleStartDate(selectedDate.value, selectedDueTime.value),
        rrule: rruleValue.value,
      }

      await store.dispatch('calendar/createTask', taskInfo)
    }
  } catch (error) {
    store.commit('calendar/SET_LAST_SUBMIT_FAILED', true)
    console.error('Create/update task failed:', error)
    notifyUnreportedError(error, 'Failed to save task')
  } finally {
    // Always release the submit button, including when the patient calendar lookup or the
    // task mutation threw.
    store.dispatch('calendar/setCreateOrUpdateEventAction', false)
    store.dispatch('calendar/setTaskLoading', false)
  }
})

watch(originalTaskInfo, (newVal) => {
  selectedPatient.value = resolveSelectedPatientId(newVal)
  description.value = newVal?.description || ''
  taskTitle.value = newVal?.title || ''
  selectedDate.value = newVal?.selectedDate || defaultDate.value
  selectedDueTime.value = newVal?.selectedDueTime || defaultDueTime.value
  if (newVal?.selectedRepeat) {
    selectedRepeat.value = newVal.selectedRepeat
  } else if (newVal?.rrule) {
    selectedRepeat.value = resolveRepeatFromRrule(newVal.rrule)
  }
  // Keep an existing recurring task's original start so re-syncing never shifts it
  const existingDtstart = resolveDtstartFromRrule(newVal?.rrule)
  if (existingDtstart) {
    recurrenceStartIso.value = existingDtstart
  }
}, { immediate: true })

watch(patientsList, () => {
  const resolvedPatientId = resolveSelectedPatientId()
  if (selectedPatient.value === resolvedPatientId) return
  selectedPatient.value = resolvedPatientId
}, { immediate: true })

// Sync task form field changes back to store so data is preserved when expanding from preview to detail
watch(
  [taskTitle, description, selectedPatient, selectedDate, selectedDueTime, selectedRepeat],
  ([title, desc, patient, date, dueTime, repeat]) => {
    const base = originalTaskInfo.value || {}
    const updatedInfo = {
      ...base,
      title: title,
      description: desc,
      selectedPatient: patient,
      selectedDate: date,
      selectedDueTime: dueTime,
      selectedRepeat: repeat,
      rrule: buildRrule(repeat, date),
    }
    store.dispatch('calendar/setCurrentTaskInfo', updatedInfo)
  }
)
</script>


<style scoped>
.form-line{
  display: flex;
  align-items: start;
  gap: 12px;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.repeat-icon {
  background-color: #C4C4C4;
  -webkit-mask: url('~@/assets/my-calendar/availability/repeat.svg') no-repeat center / contain;
  mask: url('~@/assets/my-calendar/availability/repeat.svg') no-repeat center / contain;
}

.note-font{
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  letter-spacing: 0.15px;
  color:#999999;
}
.title-div{
  flex: 1;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333333;
  border-bottom: 1px solid #E0E0E0;
  padding-bottom: 8px;
}

.patient-select :deep(.el-select__wrapper){
  box-shadow: none !important;
  border-bottom: 1px solid #E0E0E0;
  border-radius: 0px;
  /* padding: 0; */
}
.time-select{
  margin: 0px;
}
.time-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border:none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 6px 8px 6px 12px;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder) {
  color: #333;
}
.time-select :deep(.el-select__selected-item.el-select__placeholder.is-transparent) {
  color: #666;
}
.description-input :deep(.el-input__wrapper){
  border:none;
  box-shadow: none !important;
  padding: 0 0 4px 0;
  height: 32px;
  border-radius: 0px;
  border-bottom: 1px solid #E0E0E0;
}
.description-input :deep(.el-input__inner){
  font-size: 14px;
  font-family: Inter;
  padding: 0;
}
.repeat-select :deep(.el-select__wrapper) {
  height: 38px;
  background: #F2F2F2;
  border: none;
  box-shadow: none !important;
  cursor: pointer;
  padding: 8px 8px 8px 12px;

}

.repeat-select :deep(.el-select__placeholder.is-transparent) {
  color: #666;
}

.repeat-select :deep(.el-select__selected-item.el-select__placeholder) {
  color: #333;
}

.repeat-row {
  width: 159px;
}

</style>

  